import { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Users, DollarSign, TrendingUp, Copy, LogOut, Video, Eye, Plus, ExternalLink, Clock, CheckCircle } from "lucide-react";
import { formatDate } from "@/lib/datetime";

interface PartnerData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  referralCode: string;
  programType: string;
  isActive: boolean;
}

interface ReferralStats {
  programType: 'referral';
  totalReferrals: number;
  activeReferrals: number;
  monthlyEarnings: number;
  totalPaid: number;
  pendingAmount: number;
  minimumPayout: number;
  canRequestPayout: boolean;
}

interface CreatorStats {
  programType: 'creator';
  totalReferrals: number;
  activeReferrals: number;
  totalVideos: number;
  activeVideos: number;
  totalViews: number;
  viewEarnings: number;
  conversionEarnings: number;
  combinedEarnings: number;
  totalPaid: number;
  pendingAmount: number;
  minimumPayout: number;
  canRequestPayout: boolean;
}

type StatsData = ReferralStats | CreatorStats;

interface CreatorVideoData {
  id: string;
  videoUrl: string;
  platform: string;
  currentViews: number;
  milestonesHit: number;
  milestonesEarned: number;
  conversions: number;
  status: string;
  submittedAt: string;
  expiresAt: string;
  lastViewUpdate: string | null;
}

export default function PartnerDashboard() {
  const [partner, setPartner] = useState<PartnerData | null>(null);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [videos, setVideos] = useState<CreatorVideoData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState('');
  const [isSubmittingVideo, setIsSubmittingVideo] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const isCreator = partner?.programType === 'creator';

  const fetchData = async () => {
    try {
      const fetches = [
        fetch('/api/ref/me'),
        fetch('/api/ref/stats'),
      ];

      const [partnerRes, statsRes] = await Promise.all(fetches);
      
      if (!partnerRes.ok || !statsRes.ok) {
        setLocation('/ref/login');
        return;
      }
      
      const partnerData = await partnerRes.json();
      const statsData = await statsRes.json();
      
      setPartner(partnerData.partner);
      setStats(statsData);

      if (partnerData.partner.programType === 'creator') {
        const videosRes = await fetch('/api/ref/videos');
        if (videosRes.ok) {
          const videosData = await videosRes.json();
          setVideos(videosData.videos || []);
        }
      }
    } catch (error) {
      console.error('Error fetching partner data:', error);
      setLocation('/ref/login');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [setLocation]);

  const handleLogout = async () => {
    try {
      await apiRequest('POST', '/api/ref/logout', {});
      toast({
        title: "Signed out",
        description: "You've been successfully signed out.",
      });
      setLocation('/ref/login');
    } catch (error) {
      toast({
        title: "Logout failed",
        description: "Unable to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const copyReferralLink = () => {
    const baseUrl = window.location.origin;
    const referralLink = `${baseUrl}?ref=${partner?.referralCode}`;
    navigator.clipboard.writeText(referralLink);
    toast({
      title: "Link copied!",
      description: "Your referral link has been copied to clipboard.",
    });
  };

  const handleSubmitVideo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingVideo || !videoUrl.trim()) return;
    setIsSubmittingVideo(true);
    try {
      const res = await apiRequest('POST', '/api/ref/videos', { videoUrl: videoUrl.trim() });
      const data = await res.json();
      if (data.video) {
        setVideos(prev => [data.video, ...prev]);
        setVideoUrl('');
        toast({ title: "Video submitted", description: "Your video has been submitted for tracking." });
      }
    } catch (error: any) {
      toast({
        title: "Submission failed",
        description: error.message || "Please provide a valid TikTok or Instagram URL.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingVideo(false);
    }
  };

  const formatNumber = (n: number) => n.toLocaleString();
  const formatCurrency = (n: number) => `$${formatNumber(n)} JMD`;

  const getDaysRemaining = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!partner || !stats) {
    return null;
  }

  const baseUrl = window.location.origin;
  const referralLink = `${baseUrl}?ref=${partner.referralCode}`;

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold" data-testid="text-partner-dashboard-title">
                  {isCreator ? 'Creator Dashboard' : 'Partner Dashboard'}
                </h1>
                {isCreator && <Badge className="bg-chart-2 text-white">Creator</Badge>}
              </div>
              <p className="text-sm text-muted-foreground" data-testid="text-partner-name">
                {partner.firstName} {partner.lastName}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="button-partner-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Your Referral Link</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={referralLink}
                  readOnly
                  className="flex-1 px-4 py-2 border rounded-md bg-muted text-sm"
                  data-testid="input-referral-link"
                />
                <Button onClick={copyReferralLink} data-testid="button-copy-link">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                {isCreator
                  ? "Share this link in your videos. You'll earn $400 JMD for each subscriber who signs up using your code."
                  : "Share this link to refer new students. You'll earn $300 JMD per month for each active referral."}
              </p>
            </CardContent>
          </Card>

          {isCreator && stats.programType === 'creator' ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Views</CardTitle>
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatNumber(stats.totalViews)}</div>
                    <p className="text-xs text-muted-foreground">
                      {stats.totalVideos} video{stats.totalVideos !== 1 ? 's' : ''} submitted
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">View Earnings</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(stats.viewEarnings)}</div>
                    <p className="text-xs text-muted-foreground">$1,000 per 10k views</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Conversion Earnings</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(stats.conversionEarnings)}</div>
                    <p className="text-xs text-muted-foreground">
                      {stats.totalReferrals} conversion{stats.totalReferrals !== 1 ? 's' : ''} ({formatCurrency(400)}/each)
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Earned</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(stats.combinedEarnings)}</div>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(stats.totalPaid)} paid out
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    Submit Video for Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitVideo} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="videoUrl">Video URL (TikTok or Instagram)</Label>
                      <div className="flex gap-2">
                        <Input
                          id="videoUrl"
                          placeholder="https://www.tiktok.com/@user/video/..."
                          value={videoUrl}
                          onChange={(e) => setVideoUrl(e.target.value)}
                          className="flex-1"
                        />
                        <Button type="submit" disabled={isSubmittingVideo || !videoUrl.trim()}>
                          {isSubmittingVideo ? "Submitting..." : "Submit Video"}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Views are tracked for 90 days from submission. You earn $1,000 JMD for every 10,000 views.
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>

              {videos.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Video className="h-5 w-5" />
                      Your Videos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-3 font-medium text-muted-foreground">Video</th>
                            <th className="pb-3 font-medium text-muted-foreground">Platform</th>
                            <th className="pb-3 font-medium text-muted-foreground text-right">Views</th>
                            <th className="pb-3 font-medium text-muted-foreground text-right">Milestones</th>
                            <th className="pb-3 font-medium text-muted-foreground text-right">Earned</th>
                            <th className="pb-3 font-medium text-muted-foreground">Status</th>
                            <th className="pb-3 font-medium text-muted-foreground">Submitted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {videos.map((video) => (
                            <tr key={video.id} className="border-b last:border-0">
                              <td className="py-3 pr-4">
                                <a
                                  href={video.videoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline flex items-center gap-1 max-w-[200px] truncate"
                                >
                                  <ExternalLink className="h-3 w-3 shrink-0" />
                                  <span className="truncate">{video.videoUrl}</span>
                                </a>
                              </td>
                              <td className="py-3 pr-4">
                                <Badge variant="secondary">
                                  {video.platform === 'tiktok' ? 'TikTok' : 'Instagram'}
                                </Badge>
                              </td>
                              <td className="py-3 pr-4 text-right font-medium">
                                {formatNumber(video.currentViews)}
                              </td>
                              <td className="py-3 pr-4 text-right">
                                {video.milestonesHit} x 10k
                              </td>
                              <td className="py-3 pr-4 text-right font-medium">
                                {formatCurrency(video.milestonesEarned)}
                              </td>
                              <td className="py-3 pr-4">
                                {video.status === 'active' ? (
                                  <Badge variant="secondary" className="bg-chart-2/10 text-chart-2">
                                    <Clock className="w-3 h-3 mr-1" />
                                    {getDaysRemaining(video.expiresAt)}d left
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Expired
                                  </Badge>
                                )}
                              </td>
                              <td className="py-3 text-muted-foreground">
                                {formatDate(video.submittedAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>How Creator Earnings Work</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h3 className="font-semibold">Conversion Earnings</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Earn $400 JMD for each subscriber who signs up using your referral code</li>
                      <li>Subscriber must stay active for 2+ months before payout</li>
                      <li>No cap on number of conversions</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold">View Earnings</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Earn $1,000 JMD for every 10,000 views on your videos</li>
                      <li>Views are tracked for 90 days from submission date</li>
                      <li>No cap on number of videos or total earnings</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold">Payout Rules</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Minimum payout: {formatCurrency(stats.minimumPayout)}</li>
                      <li>Balance expires after 12 months of inactivity</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Active Referrals</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" data-testid="text-active-referrals">
                      {stats.programType === 'referral' ? stats.activeReferrals : 0}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {stats.totalReferrals} total referrals
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Monthly Earnings</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" data-testid="text-monthly-earnings">
                      {formatCurrency(stats.programType === 'referral' ? stats.monthlyEarnings : 0)}
                    </div>
                    <p className="text-xs text-muted-foreground">$300 JMD per active referral</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" data-testid="text-total-paid">
                      {formatCurrency(stats.totalPaid)}
                    </div>
                    <p className="text-xs text-muted-foreground">Lifetime payouts</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>How It Works</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <h3 className="font-semibold">Earning Structure</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Earn $300 JMD per month for each active referral</li>
                      <li>Minimum payout threshold: {formatCurrency(stats.minimumPayout)}</li>
                      <li>Payouts processed monthly via bank transfer</li>
                      <li>30-day attribution window for new signups</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold">Referral Requirements</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Referred student must sign up using your referral link</li>
                      <li>Student must maintain an active paid subscription</li>
                      <li>Earnings are calculated monthly based on active subscriptions</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
